import React, { useState } from 'react';
import { Box, Stack } from '@mui/material';

import { MaterialPropertyValuePair } from '@paragon/client-tools';
import { BearingEnvelope } from '@paragon/contract-library';
import { PropertiesPanel } from '@paragon/layout-models';
import { PartialPartial } from '@paragon/utilities';

import { getBearingEnvelopeSelectableType } from '../../../../../../library/selection/types';
import { TransformationButtons } from '../../../../../../library/transformations/controls/TransformationButtons';
import { getBearingEnvelopeUserVisibleTerm } from '../../../../../../library/utilities/bearingEnvelopeUserTerm';
import { editBearingEnvelopes } from '../../../../../../store/actions/layout/bearingEnvelopeActions';
import { beginTransformation } from '../../../../../../store/actions/layout/transformationActions';
import { isLayoutModeViewOnly } from '../../../../../../store/selectors/layoutModeSelectors';
import { propertiesShouldBeViewOnly } from '../../../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../../../hooks';
import { EnvelopeJustificationToggle } from '../../../elements/EnvelopeJustificationToggle';
import { Divider } from '../../../replace-materials/layout';
import { PropertiesPanelMenuButton } from '../../PropertiesPanelMenuButton';
import { BearingEnvelopeCommonProperties } from './BearingEnvelopeCommonProperties';
import { BearingEnvelopePointProperties } from './BearingEnvelopePointProperties';

export interface BearingEnvelopePropertiesProps {
  disabled?: boolean;

  properties: PartialPartial<Omit<BearingEnvelope, 'guid' | 'name'>, 'leftPoint' | 'rightPoint'>;
  onChange: (changes: Partial<Omit<BearingEnvelope, 'guid' | 'name' | 'bearingType'>>) => void;
}

export function BearingEnvelopeProperties(props: { value: BearingEnvelope }) {
  const dispatch = useAppDispatch();
  const viewOnlyProperties = useAppSelector(propertiesShouldBeViewOnly);
  const viewOnlyLayout = useAppSelector(isLayoutModeViewOnly);
  const viewOnly = viewOnlyProperties || viewOnlyLayout;

  const [isSaving, setIsSaving] = useState(false);

  async function saveChangedProperties(editedProperties: Partial<Omit<BearingEnvelope, 'guid'>>) {
    if (isSaving)
      throw new Error(
        'Overlapping save of bearing envelope prevented. Someone forgot to disable a control when saving.',
      );

    setIsSaving(true);

    try {
      await dispatch(editBearingEnvelopes({ guid: props.value.guid, editedProperties }));
    } finally {
      setIsSaving(false);
    }
  }

  const disabled = viewOnly || isSaving;
  const selectableType = getBearingEnvelopeSelectableType(props.value);
  const userVisibleTerm = getBearingEnvelopeUserVisibleTerm(props.value.bearingType);

  return (
    <PropertiesPanel
      title={`${userVisibleTerm} Properties`}
      titleButton={<PropertiesPanelMenuButton type={selectableType} guid={props.value.guid} />}
    >
      <Stack spacing={2} className="clickable-during-picking">
        <TransformationButtons disabled={viewOnly} />

        <Divider />

        <MaterialPropertyValuePair property="Name" value={props.value.name} />

        <BearingEnvelopeCommonProperties
          disabled={disabled}
          properties={props.value}
          onChange={saveChangedProperties}
        />

        <Box>
          <EnvelopeJustificationToggle
            value={props.value.justification}
            disabled={disabled}
            onChange={(justification) => saveChangedProperties({ justification })}
          />
        </Box>

        <Divider />

        <BearingEnvelopePointProperties
          disabled={disabled}
          properties={props.value}
          onChange={saveChangedProperties}
          onMoveRequested={() => {
            dispatch(beginTransformation('movement'));
          }}
        />
      </Stack>
    </PropertiesPanel>
  );
}
